'use client';

import { useCallback, useMemo } from 'react';
import {
  UserMenuInitialTab,
  type OpenUserMenuOptions,
} from '@/contexts/layout';
import { SessionsDisplayMode } from '@/types/frontend-types';
import type { TeamDetail } from '@cabal/shared/domain/session/team/types.js';
import { useSessionsModalState } from './useSessionsModalState';
import { useUserMenuModalState } from './useUserMenuModalState';

interface UseCpagelayoutModalStateInput {
  externalModalVisible?: boolean;
  onExternalHamburgerClick?: () => void;
  onExternalModalClose?: () => void;
  onExternalProfileClick?: () => void;
  onReplicateTeam?: (teamConfig: TeamDetail) => void;
  openUserMenuRequestId?: number;
}

interface LayoutActionsValue {
  openUserMenu: (options?: OpenUserMenuOptions) => void;
}

interface UseCpagelayoutModalStateOutput {
  displayMode: SessionsDisplayMode;
  isAnyModalOpen: boolean;
  isCenterModalOpen: boolean;
  isSearchInitiated: boolean;
  isSessionsModalOpen: boolean;
  isUserMenuModalOpen: boolean;
  layoutActionsValue: LayoutActionsValue;
  onCenterModalClose: () => void;
  onDisplayModeToggle: () => void;
  onHamburgerClick: () => void;
  onModalClose: () => void;
  onProfileClick: () => void;
  onReplicateTeam: (teamConfig: TeamDetail) => void;
  onSearchQueryChange: (query: string) => void;
  onSearchToggle: () => void;
  onSessionCountChange: (count: number | null) => void;
  onSessionsModalClose: () => void;
  onUserMenuModalClose: () => void;
  requestUserMenuClose: () => boolean | void;
  onUserMenuUnsavedChangesChange: (hasUnsavedChanges: boolean) => void;
  searchQuery: string;
  sessionCount: number | null;
  userMenuInitialTab: UserMenuInitialTab;
}

enum ModalCloseTarget {
  Center = 'center',
  None = 'none',
  Sessions = 'sessions',
  UserMenu = 'userMenu',
}

interface ModalCloseTargetInput {
  externalModalVisible?: boolean;
  isSessionsModalOpen: boolean;
  isUserMenuModalOpen: boolean;
}

function getModalCloseTarget({
  externalModalVisible,
  isSessionsModalOpen,
  isUserMenuModalOpen,
}: ModalCloseTargetInput): ModalCloseTarget {
  if (externalModalVisible) {
    return ModalCloseTarget.Center;
  }

  if (isSessionsModalOpen) {
    return ModalCloseTarget.Sessions;
  }

  if (isUserMenuModalOpen) {
    return ModalCloseTarget.UserMenu;
  }

  return ModalCloseTarget.None;
}

export function useCpagelayoutModalState({
  externalModalVisible,
  onExternalHamburgerClick,
  onExternalModalClose,
  onExternalProfileClick,
  onReplicateTeam,
  openUserMenuRequestId,
}: UseCpagelayoutModalStateInput): UseCpagelayoutModalStateOutput {
  const sessionsModal = useSessionsModalState();
  const userMenuModal = useUserMenuModalState({
    onSessionsModalClose: sessionsModal.onSessionsModalClose,
    openUserMenuRequestId,
  });

  const onCenterModalClose = useCallback((): void => {
    onExternalModalClose?.();
  }, [onExternalModalClose]);

  const openSessionsModal = useCallback((): void => {
    userMenuModal.closeUserMenu();
    sessionsModal.openSessionsModal();
  }, [sessionsModal, userMenuModal]);

  const modalCloseHandlers = useMemo<Record<ModalCloseTarget, () => void>>(() => ({
    [ModalCloseTarget.Center]: onCenterModalClose,
    [ModalCloseTarget.None]: () => undefined,
    [ModalCloseTarget.Sessions]: sessionsModal.onSessionsModalClose,
    [ModalCloseTarget.UserMenu]: userMenuModal.requestUserMenuClose,
  }), [onCenterModalClose, sessionsModal, userMenuModal]);

  const onModalClose = useCallback((): void => {
    modalCloseHandlers[getModalCloseTarget({
      externalModalVisible,
      isSessionsModalOpen: sessionsModal.isSessionsModalOpen,
      isUserMenuModalOpen: userMenuModal.isUserMenuModalOpen,
    })]();
  }, [
    externalModalVisible,
    modalCloseHandlers,
    sessionsModal,
    userMenuModal,
  ]);

  const onHamburgerClick = useCallback((): void => {
    if (onExternalHamburgerClick) {
      userMenuModal.closeUserMenu();
      onExternalHamburgerClick();
      return;
    }

    openSessionsModal();
  }, [onExternalHamburgerClick, openSessionsModal, userMenuModal]);

  const onProfileClick = useCallback((): void => {
    if (onExternalProfileClick) {
      onExternalProfileClick();
      return;
    }

    userMenuModal.openUserMenu();
  }, [onExternalProfileClick, userMenuModal]);

  const onReplicateTeamCallback = useCallback((teamConfig: TeamDetail): void => {
    onModalClose();
    onReplicateTeam?.(teamConfig);
  }, [onModalClose, onReplicateTeam]);

  return {
    displayMode: sessionsModal.displayMode,
    isAnyModalOpen: Boolean(
      externalModalVisible || sessionsModal.isSessionsModalOpen || userMenuModal.isUserMenuModalOpen,
    ),
    isCenterModalOpen: Boolean(externalModalVisible),
    isSearchInitiated: sessionsModal.isSearchInitiated,
    isSessionsModalOpen: sessionsModal.isSessionsModalOpen,
    isUserMenuModalOpen: userMenuModal.isUserMenuModalOpen,
    layoutActionsValue: userMenuModal.layoutActionsValue,
    onCenterModalClose,
    onDisplayModeToggle: sessionsModal.onDisplayModeToggle,
    onHamburgerClick,
    onModalClose,
    onProfileClick,
    onReplicateTeam: onReplicateTeamCallback,
    onSearchQueryChange: sessionsModal.onSearchQueryChange,
    onSearchToggle: sessionsModal.onSearchToggle,
    onSessionCountChange: sessionsModal.onSessionCountChange,
    onSessionsModalClose: sessionsModal.onSessionsModalClose,
    onUserMenuModalClose: userMenuModal.onUserMenuModalClose,
    requestUserMenuClose: userMenuModal.requestUserMenuClose,
    onUserMenuUnsavedChangesChange: userMenuModal.onUserMenuUnsavedChangesChange,
    searchQuery: sessionsModal.searchQuery,
    sessionCount: sessionsModal.sessionCount,
    userMenuInitialTab: userMenuModal.userMenuInitialTab,
  };
}
